import { Location, locationName, locationUserPermission } from '@cobliteam/common-types';
import useUser from '@cobliteam/hooks/src/useEntity/useUser';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import type FeatureFlags from '@cobliteam/hooks/src/useFeatureFlags/featureFlags';
import { lazy } from '@cobliteam/lazy-with-retries';
import { type Permission, usePermissions } from '@cobliteam/permissions';
import AccessDenied from '@cobliteam/ui-business/components/AccessDenied';
import { type ComponentType, Suspense } from 'react';
import { Redirect, Route, type RouteComponentProps, useLocation } from 'react-router-dom';
import LoadingPage from '../../pages/LoadingPage';
import MaintenancePage from '../../pages/MaintenancePage';

import routes, { type IRoute } from '../../routes';

// Lazy loading of Providers - monted only on routes / pages that requires their
// provided functionalities. Flagged by `needsBlueprint` and `needsGoogleMaps`.
const BlueprintProvider = lazy(() => import('../../providers/BlueprintProvider/BlueprintProvider').then((m) => ({ default: m.BlueprintProvider })));
const GoogleMapsProvider = lazy(() => import('../../providers/GoogleMapsProvider/GoogleMapsProvider').then((m) => ({ default: m.GoogleMapsProvider })));

interface PrivateRouteProps {
  route: IRoute;
}

const getSafeUnauthorizedFallback = (search: string): string | null => {
  const fallback = new URLSearchParams(search).get('unauthorizedFallback');
  if (!fallback) return null;
  if (!fallback.startsWith('/') || fallback.startsWith('//') || fallback.includes('..')) return null;
  return fallback;
};

const recursivePaths = (routesDef: IRoute[]): IRoute[] => {
  const paths: IRoute[] = [];
  for (const r of routesDef) {
    paths.push(r);
    if (r.routes) paths.push(...recursivePaths(r.routes));
  }
  return paths;
};

const PUBLIC_ROUTES: readonly Location[] = [Location.LOGIN, Location.TOKEN_VALIDATION, Location.PASSWORD_RECOVERY, Location.PASSWORD_RESET, Location.MFA_CHALLENGE];

const PrivateRoute = ({ route }: PrivateRouteProps) => {
  const { isAccessAllowed } = usePermissions();
  const { hasGroupsRestriction } = useUser();
  const location = useLocation();
  const featureToggleFlags = useFeatureFlags();

  // `routes` aggregates route definitions from many packages, each with its own IRoute type.
  // Cast at the call boundary — shape is compatible in practice, only structural mismatches
  // between per-package IRoute copies would break here.
  const routerSelected: IRoute | undefined = recursivePaths(routes as IRoute[]).find((r: IRoute) => {
    const pathRegex = new RegExp(`^${r.path?.replace(/:[a-zA-Z]+/g, '[a-zA-Z0-9-]+')}$`);
    return pathRegex.test(location.pathname);
  });

  // check if the router is in the maintenance feature flag
  const maintenanceRoutes = Array.isArray(featureToggleFlags.maintenance) ? featureToggleFlags.maintenance : [];
  const isMaintenance = maintenanceRoutes.some((router: { url?: string }) => router.url === routerSelected?.path);

  // redirect to home / if path is empty
  if (location.pathname === Location.EMPTY) return <Redirect to={Location.HOME} />;

  if (routerSelected?.featureFlag && featureToggleFlags[routerSelected.featureFlag as keyof FeatureFlags] === false) return <Redirect to={Location.NOT_FOUND} />;

  // after login and router is public - hotfix
  if (!routerSelected && PUBLIC_ROUTES.includes(location.pathname as Location)) return <Redirect to={Location.HOME} />;

  // router not found
  if (!routerSelected) return <Redirect to={Location.NOT_FOUND} />;

  const routePath = routerSelected.path;
  const requiredPermission = locationUserPermission[routePath] as Permission | undefined;
  const isAllowed = requiredPermission ? isAccessAllowed(requiredPermission) : true;
  const userHasGroupsRestriction = route.hasGroupsRestriction === true && hasGroupsRestriction === true;

  // change title
  if (location.pathname === routePath) {
    document.title = `Cobli - ${routerSelected.title || locationName[routePath] || ''}`;
  }

  return (
    <Route
      path={route.path}
      render={(props: RouteComponentProps) => {
        if (!isAllowed) {
          const fallback = getSafeUnauthorizedFallback(location.search);
          if (fallback) return <Redirect to={fallback} />;
          return <AccessDenied pageDescription={routerSelected.accessDeniedDescription} />;
        }

        if (userHasGroupsRestriction) return <AccessDenied />;
        if (isMaintenance) return <MaintenancePage title={`${routerSelected.title || locationName[routePath] || ''}`} path={routePath} />;

        const Component = route.component as ComponentType<RouteComponentProps & { routes?: IRoute[] }>;
        const content = <Component {...props} routes={route.routes} />;

        let wrapped = content;

        if (route.needsGoogleMaps) {
          wrapped = (
            <Suspense fallback={<LoadingPage />}>
              <GoogleMapsProvider>{wrapped}</GoogleMapsProvider>
            </Suspense>
          );
        }

        if (route.needsBlueprint) {
          wrapped = (
            <Suspense fallback={<LoadingPage />}>
              <BlueprintProvider>{wrapped}</BlueprintProvider>
            </Suspense>
          );
        }

        return wrapped;
      }}
      exact={route.exact}
    />
  );
};

export default PrivateRoute;
